// HeaderNavigation.js - Header navigation buttons with walkthrough support
import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { CopilotStep, walkthroughable } from 'react-native-copilot';

import { HEADER } from './header/headerTheme';
import NavTab from './header/NavTab';
import HeaderNavDropdown from './header/HeaderNavDropdown';
import { shouldShowProgramsMenu } from './header/programsMenu';

const WalkthroughableTouchableOpacity = walkthroughable(TouchableOpacity);

// Student navigation buttons (Home + Curriculum + Collections + Programs ▾).
// "Learning" was split into two tabs on 2026-09-17 per teacher feedback
// (Round 2 of the header redesign) — see
// docs/superpowers/specs/2026-09-15-header-redesign.md, WP7. The separate
// Lessons and Journeys tabs were folded into one "Programs ▾" dropdown
// (which also reaches STEM Missions and Summer Camp days) on 2026-09-17 —
// see docs/superpowers/specs/2026-09-16-home-redesign.md §11 WP10.
export const StudentNavButtons = ({
  activeModule,
  setActiveModule,
  onCurriculumPress,
  onCollectionsPress,
  // Hidden for students scoped to collections_only content.
  showCurriculum = true,
  // Dimmed/blocked when the student has no collections at all.
  collectionsDisabled = false,
  viewMode = "collections",
  // "Programs ▾" — Lessons, Discovery Journeys, STEM Missions, Summer Camp.
  // Built by components/header/programsMenu.js's buildProgramItems; hidden
  // entirely when it has no real (non-section) item. isActive is computed
  // by the caller (AITutorApp.js) since it depends on state (showJourney)
  // this component does not have.
  programItems = [],
  programsActive = false,
  showLabels = true,
  styles: h,
  isDarkMode,
  disabled = false,
  t = (s) => s,
}) => {
  // Computed from the raw `programItems` prop, NOT `effectiveProgramItems`
  // below: `disabled` (a test in progress) force-disables every leaf on
  // purpose, and the menu must still render then — only its trigger goes
  // inert. Gating on the post-`disabled` array would hide the whole menu
  // during a test, which is not the intent.
  const hasProgramItems = shouldShowProgramsMenu(programItems);
  // A test in progress blocks navigation everywhere else in this bar; for a
  // dropdown the trigger still opens (so the menu itself is inspectable),
  // but every leaf item is disabled so nothing can be selected out from
  // under the test.
  const effectiveProgramItems = disabled
    ? programItems.map((item) =>
        item.type === "section" ? item : { ...item, disabled: true }
      )
    : programItems;
  const learningActive = ["learning", "video", "pwo"].includes(activeModule);

  const collectionsTab = (
    <NavTab
      as={showCurriculum ? TouchableOpacity : WalkthroughableTouchableOpacity}
      onPress={disabled || collectionsDisabled ? undefined : onCollectionsPress}
      accessibilityLabel={t("Go to collections")}
      title={collectionsDisabled ? t("No collections yet") : t("Collections")}
      disabled={disabled || collectionsDisabled}
      dimmed={disabled || collectionsDisabled}
      isActive={learningActive && viewMode === "collections"}
      showLabels={showLabels}
      iconName="folder-multiple-outline"
      label={t("Collections")}
      styles={h}
    />
  );

  return (
    <>
      {/* Home button */}
      <CopilotStep
        text={t("This is your Home. View your dashboard, recent activity, and quick actions here.")}
        order={1}
        name="home-button"
      >
        <NavTab
          as={WalkthroughableTouchableOpacity}
          onPress={disabled ? undefined : () => setActiveModule("home")}
          accessibilityLabel={t("Go to home")}
          title={t("Home")}
          disabled={disabled}
          dimmed={disabled}
          isActive={activeModule === "home"}
          showLabels={showLabels}
          iconName="home"
          label={t("Home")}
          styles={h}
        />
      </CopilotStep>

      {/* Curriculum button - hidden for collections_only students */}
      {showCurriculum && (
        <CopilotStep
          text={t("Browse your curriculum by chapter: videos, 3D models and quizzes for every topic.")}
          order={2}
          name="learning-button"
        >
          <NavTab
            as={WalkthroughableTouchableOpacity}
            onPress={disabled ? undefined : onCurriculumPress}
            accessibilityLabel={t("Go to curriculum")}
            title={t("Curriculum")}
            disabled={disabled}
            dimmed={disabled}
            isActive={learningActive && viewMode === "curriculum"}
            showLabels={showLabels}
            iconName="book-open-variant"
            label={t("Curriculum")}
            styles={h}
          />
        </CopilotStep>
      )}

      {/* Collections button - always rendered. When Curriculum is hidden
          (collections_only students), this tab carries the learning-button
          copilot step instead, so the tour still has exactly one step 2. */}
      {showCurriculum ? (
        collectionsTab
      ) : (
        <CopilotStep
          text={t("Your learning modules live here.")}
          order={2}
          name="learning-button"
        >
          {collectionsTab}
        </CopilotStep>
      )}

      {/* Programs ▾ — Lessons, Discovery Journeys, STEM Missions, Summer Camp */}
      {hasProgramItems && (
        <HeaderNavDropdown
          label={t("Programs")}
          icon="compass-outline"
          isActive={programsActive}
          showLabels={showLabels}
          isDarkMode={isDarkMode}
          styles={h}
          t={t}
          testID="student-nav-programs"
          items={effectiveProgramItems}
        />
      )}
    </>
  );
};

// Teacher-specific navigation buttons (Dashboard, Create ▾, Manage ▾).
// "Tests"/"Collections"/"Students" moved into two dropdowns on 2026-09-17
// per teacher feedback (Round 2 of the header redesign) — see
// docs/superpowers/specs/2026-09-15-header-redesign.md, WP7.
export const TeacherNavButtons = ({
  activeModule,
  setActiveModule,
  handleTeacherDashboard,
  currentCurriculum,
  showLabels = true,
  styles: h,
  isDarkMode,
  showLessonPlans = false,
  onOpenLessonPlans,
  // A test in progress blocks navigation (AIT-299). Same pattern as
  // StudentNavButtons: dropdown triggers still open, every leaf is disabled.
  disabled = false,
  t = (s) => s,
}) => {
  const createItems = [
    {
      key: "tests",
      label: t("Tests"),
      icon: "clipboard-text-outline",
      disabled: !currentCurriculum,
      active: activeModule === "testQuestions",
      onPress: () => setActiveModule("testQuestions"),
    },
    {
      key: "collections",
      label: t("Collections"),
      icon: "folder-multiple-outline",
      active: activeModule === "collections",
      onPress: () => setActiveModule("collections"),
    },
    ...(showLessonPlans
      ? [
          {
            key: "lessonPlans",
            label: t("Lesson plans"),
            icon: "clipboard-text-outline",
            active: activeModule === "lessonPlans",
            onPress: onOpenLessonPlans,
          },
        ]
      : []),
  ];

  const manageItems = [
    {
      key: "students",
      label: t("Students"),
      icon: "account-multiple-plus-outline",
      active: activeModule === "manageStudents",
      onPress: () => setActiveModule("manageStudents"),
    },
  ];

  const lockLeaves = (items) =>
    disabled ? items.map((item) => ({ ...item, disabled: true })) : items;
  const effectiveCreateItems = lockLeaves(createItems);
  const effectiveManageItems = lockLeaves(manageItems);

  return (
    <>
      {/* Dashboard button */}
      <CopilotStep
        text={t("View your Teacher Dashboard to monitor student progress, engagement metrics, and class performance.")}
        order={3}
        name="dashboard-button"
      >
        <NavTab
          as={WalkthroughableTouchableOpacity}
          onPress={disabled ? undefined : handleTeacherDashboard}
          title={t("Dashboard")}
          disabled={disabled}
          dimmed={disabled}
          isActive={activeModule === "teacherDashboard"}
          showLabels={showLabels}
          iconName="account-tie"
          label={t("Dashboard")}
          styles={h}
        />
      </CopilotStep>

      {/* Create ▾ - Tests, Collections, Lesson plans (when enabled) */}
      <HeaderNavDropdown
        label={t("Create")}
        icon="plus-box-outline"
        isActive={["testQuestions", "collections"].includes(activeModule)}
        showLabels={showLabels}
        isDarkMode={isDarkMode}
        styles={h}
        t={t}
        testID="teacher-nav-create"
        items={effectiveCreateItems}
        copilotName="tests-button"
        copilotOrder={4}
        copilotText={t("Create tests, collections and lesson plans for your classes.")}
      />

      {/* Manage ▾ - Students */}
      <HeaderNavDropdown
        label={t("Manage")}
        icon="account-cog-outline"
        isActive={activeModule === "manageStudents"}
        showLabels={showLabels}
        isDarkMode={isDarkMode}
        styles={h}
        t={t}
        testID="teacher-nav-manage"
        items={effectiveManageItems}
        copilotName="students-button"
        copilotOrder={6}
        copilotText={t("Manage your students and classes.")}
      />
    </>
  );
};

// Hamburger menu items for narrow screens (teacher)
export const TeacherHamburgerMenuItems = ({
  activeModule,
  setActiveModule,
  handleTeacherDashboard,
  currentCurriculum,
  setHamburgerMenuOpen,
  styles: h,
  showLessonPlans = false,
  onOpenLessonPlans,
  disabled = false,
  t = (s) => s,
}) => {
  const lock = disabled ? { disabled: true } : {};
  const dim = disabled ? { opacity: 0.5 } : null;
  return (
    <>
      <TouchableOpacity
        {...lock}
        onPress={disabled ? undefined : () => { handleTeacherDashboard(); setHamburgerMenuOpen(false); }}
        style={[h.hamburgerMenuItem, activeModule === "teacherDashboard" && h.hamburgerMenuItemActive, dim]}
        title={t("Dashboard")}
      >
        <MaterialCommunityIcons name="account-tie" size={22} color="#fff" />
        <Text style={h.hamburgerMenuText}>{t("Dashboard")}</Text>
      </TouchableOpacity>

      <TouchableOpacity
        disabled={disabled || !currentCurriculum}
        onPress={disabled ? undefined : () => { setActiveModule("testQuestions"); setHamburgerMenuOpen(false); }}
        style={[
          h.hamburgerMenuItem,
          activeModule === "testQuestions" && h.hamburgerMenuItemActive,
          (disabled || !currentCurriculum) && { opacity: 0.5 }
        ]}
        title={t("Create a test")}
      >
        <MaterialCommunityIcons name="clipboard-text-outline" size={22} color="#fff" />
        <Text style={h.hamburgerMenuText}>{t("Create a test")}</Text>
      </TouchableOpacity>

      <TouchableOpacity
        {...lock}
        onPress={disabled ? undefined : () => { setActiveModule("collections"); setHamburgerMenuOpen(false); }}
        style={[h.hamburgerMenuItem, activeModule === "collections" && h.hamburgerMenuItemActive, dim]}
        title={t("Create a collection")}
      >
        <MaterialCommunityIcons name="folder-multiple-outline" size={22} color="#fff" />
        <Text style={h.hamburgerMenuText}>{t("Create a collection")}</Text>
      </TouchableOpacity>

      {showLessonPlans && (
        <TouchableOpacity
          {...lock}
          onPress={disabled ? undefined : () => { if (onOpenLessonPlans) onOpenLessonPlans(); setHamburgerMenuOpen(false); }}
          style={[h.hamburgerMenuItem, activeModule === "lessonPlans" && h.hamburgerMenuItemActive, dim]}
          title={t("Lesson plans")}
        >
          <MaterialCommunityIcons name="clipboard-text-outline" size={22} color={HEADER.onBrand} />
          <Text style={h.hamburgerMenuText}>{t("Lesson plans")}</Text>
        </TouchableOpacity>
      )}

      <TouchableOpacity
        {...lock}
        onPress={disabled ? undefined : () => { setActiveModule("manageStudents"); setHamburgerMenuOpen(false); }}
        style={[h.hamburgerMenuItem, activeModule === "manageStudents" && h.hamburgerMenuItemActive, dim]}
        title={t("Students")}
      >
        <MaterialCommunityIcons name="account-multiple-plus-outline" size={22} color="#fff" />
        <Text style={h.hamburgerMenuText}>{t("Students")}</Text>
      </TouchableOpacity>
    </>
  );
};

export default {
  StudentNavButtons,
  TeacherNavButtons,
  TeacherHamburgerMenuItems,
};
